<template>
  <div data-private class="space-y-4">
    <!-- Report Selector -->
    <div class="bg-white rounded-lg border border-gray-200 p-5">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-900">Compare Attribution Reports</h3>
        <ExportMenu :disabled="!flows.length" :exports="[{ type: 'excel', action: exportCSV, loading: exporting }]" />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Report A Selector -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">Report A (Earlier)</label>
          <select
            v-model="selectedReportA"
            @change="fetchComparison"
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
          >
            <option v-for="report in sortedReports" :key="report.key" :value="report.key">
              {{ report.contract_id }} - {{ formatReportDate(report.report_dt) }} ({{ report.patient_count?.toLocaleString() }} patients)
            </option>
          </select>
          <div v-if="reportAData" class="mt-2 text-sm font-semibold text-gray-900">
            {{ reportAData.total_patients?.toLocaleString() || 0 }} patients
          </div>
        </div>

        <!-- Report B Selector -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">Report B (Later)</label>
          <select
            v-model="selectedReportB"
            @change="fetchComparison"
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
          >
            <option v-for="report in sortedReports" :key="report.key" :value="report.key">
              {{ report.contract_id }} - {{ formatReportDate(report.report_dt) }} ({{ report.patient_count?.toLocaleString() }} patients)
            </option>
          </select>
          <div v-if="reportBData" class="mt-2 text-sm font-semibold text-gray-900">
            {{ reportBData.total_patients?.toLocaleString() || 0 }} patients
          </div>
        </div>
      </div>

      <!-- Net Change Summary -->
      <div v-if="reportAData && reportBData" class="mt-4 p-3 bg-gray-50 rounded-lg">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Net Change:</span>
          <span :class="netChange >= 0 ? 'text-emerald-600' : 'text-rose-600'" class="font-semibold">
            {{ netChange >= 0 ? '+' : '' }}{{ netChange.toLocaleString() }}
            ({{ netChangePercent >= 0 ? '+' : '' }}{{ netChangePercent }}%)
          </span>
        </div>
      </div>

    </div>

    <!-- Key Insights (surfaced at top — high-signal summary) -->
    <div v-if="flows.length > 0 && !loading" class="bg-primary-50 rounded-lg border border-primary-200 p-5">
      <h4 class="text-sm font-semibold text-gray-900 mb-3">Key Insights</h4>
      <div class="space-y-2 text-sm">
        <div v-if="keptFlow" class="flex items-start gap-2">
          <span class="text-emerald-600 font-bold">✓</span>
          <span class="text-gray-700">
            <span class="font-semibold">{{ ((keptFlow.value / reportAData.total_patients) * 100).toFixed(1) }}% retention rate</span>
            - {{ keptFlow.value.toLocaleString() }} patients remained attributed
          </span>
        </div>

        <div v-if="competitionLoss > 0" class="flex items-start gap-2">
          <span class="text-rose-600 font-bold">⚠</span>
          <span class="text-gray-700">
            <span class="font-semibold">{{ competitionLoss.toLocaleString() }} lost to competition</span>
            - patients getting care elsewhere
          </span>
        </div>

        <div v-if="disengagedLoss > 0" class="flex items-start gap-2">
          <span class="text-amber-600 font-bold">•</span>
          <span class="text-gray-700">
            <span class="font-semibold">{{ disengagedLoss.toLocaleString() }} disengaged</span>
            - no claims activity after loss
          </span>
        </div>

        <div v-if="newFlow || returningFlow" class="flex items-start gap-2">
          <span class="text-blue-600 font-bold">+</span>
          <span class="text-gray-700">
            <span class="font-semibold">{{ totalGained.toLocaleString() }} patients gained</span>
            ({{ newFlow?.value?.toLocaleString() || 0 }} new, {{ returningFlow?.value?.toLocaleString() || 0 }} returning)
          </span>
        </div>
      </div>
    </div>

    <!-- Sankey Diagram -->
    <div v-if="sankeyData && !loading" class="bg-white rounded-lg border border-gray-200 p-5">
      <h3 class="text-sm font-semibold text-gray-900 mb-4">Patient Flow</h3>
      <div class="h-[500px] relative">
        <SankeyDiagram
          :chart-data="sankeyData"
          :config="sankeyConfig"
        />
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading comparison..." />

    <!-- Flow Summary -->
    <div v-else-if="flows.length > 0" class="space-y-4">
      <!-- Kept Patients -->
      <div v-if="keptFlow"
           class="bg-white rounded-lg border border-gray-200 p-5 hover:bg-gray-50 transition-colors cursor-pointer"
           @click="viewPatients(keptFlow)">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-sm font-semibold text-gray-900">Kept Patients</h4>
            <p class="text-xs text-gray-500 mt-1">Patients in both reports</p>
          </div>
          <div class="flex items-center gap-3">
            <div class="text-3xl font-semibold text-emerald-600">
              {{ keptFlow.value.toLocaleString() }}
            </div>
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          {{ ((keptFlow.value / reportAData.total_patients) * 100).toFixed(1) }}% retention rate
        </div>
      </div>

      <!-- Lost Patients Breakdown -->
      <div v-if="lostFlows.length > 0" class="bg-white rounded-lg border border-gray-200 p-5">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h4 class="text-sm font-semibold text-gray-900">Lost Patients</h4>
            <p class="text-xs text-gray-500 mt-1">Patients in Report A but not Report B</p>
          </div>
          <div class="text-3xl font-semibold text-rose-600">
            {{ totalLost.toLocaleString() }}
          </div>
        </div>

        <div class="space-y-2">
          <div v-for="flow in lostFlows" :key="flow.target"
               class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
               @click="viewPatients(flow)">
            <div class="flex-1">
              <div class="text-sm font-medium text-gray-900">{{ getLossLabel(flow.target) }}</div>
              <div class="text-xs text-gray-500">{{ getLossDescription(flow.target) }}</div>
            </div>
            <div class="flex items-center gap-3">
              <div class="text-right">
                <div class="text-lg font-semibold text-gray-900">{{ flow.value.toLocaleString() }}</div>
                <div class="text-xs text-gray-500">
                  {{ ((flow.value / totalLost) * 100).toFixed(1) }}% of losses
                </div>
              </div>
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- New Patients -->
      <div v-if="newFlow"
           class="bg-white rounded-lg border border-gray-200 p-5 hover:bg-gray-50 transition-colors cursor-pointer"
           @click="viewPatients(newFlow)">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-sm font-semibold text-gray-900">New Patients</h4>
            <p class="text-xs text-gray-500 mt-1">First time attributed to this entity</p>
          </div>
          <div class="flex items-center gap-3">
            <div class="text-3xl font-semibold text-blue-600">
              {{ newFlow.value.toLocaleString() }}
            </div>
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          {{ ((newFlow.value / reportBData.total_patients) * 100).toFixed(1) }}% of Report B panel
        </div>
      </div>

      <!-- Returning Patients -->
      <div v-if="returningFlow"
           class="bg-white rounded-lg border border-gray-200 p-5 hover:bg-gray-50 transition-colors cursor-pointer"
           @click="viewPatients(returningFlow)">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-sm font-semibold text-gray-900">Returning Patients</h4>
            <p class="text-xs text-gray-500 mt-1">Previously attributed, left, now back</p>
          </div>
          <div class="flex items-center gap-3">
            <div class="text-3xl font-semibold text-cyan-600">
              {{ returningFlow.value.toLocaleString() }}
            </div>
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          {{ ((returningFlow.value / reportBData.total_patients) * 100).toFixed(1) }}% of Report B panel
        </div>
      </div>

    </div>

    <!-- Empty State -->
    <div v-else-if="!loading" class="bg-white rounded-lg border border-gray-200 p-12 text-center">
      <div class="text-gray-400 text-sm">No data available for comparison</div>
      <div class="text-xs text-gray-500 mt-2">Select different report dates</div>
    </div>

    <!-- Patient List Modal -->
    <div v-if="showPatientModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closePatientModal">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" @click="closePatientModal"></div>

        <div class="relative bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-5xl">
          <!-- Modal Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ modalSubtitle }}</p>
            </div>
            <button @click="closePatientModal" class="text-gray-400 hover:text-gray-500">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Modal Content -->
          <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <LoadingIndicator v-if="loadingPatients" message="Loading patients..." />

            <table v-else-if="patientList.length > 0" class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MBI</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PCP</th>
                  <th v-if="selectedCategory === 'lost_competition'" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competing Provider</th>
                  <th v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits After</th>
                  <th v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Claim</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="patient in patientList" :key="patient.bene_mbi_id" class="hover:bg-gray-50">
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="sensitive text-sm font-medium text-gray-900">
                      {{ patient.bene_last_name }}, {{ patient.bene_first_name }}
                    </div>
                    <div v-if="patient.bene_death_dt" class="text-xs text-rose-500">
                      Deceased {{ formatDate(patient.bene_death_dt) }}
                    </div>
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ formatDate(patient.bene_dob) }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {{ patient.bene_mbi_id }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ patient.facility_name || '-' }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ patient.pcp_name || '-' }}
                  </td>
                  <td v-if="selectedCategory === 'lost_competition'" class="px-4 py-3 whitespace-nowrap">
                    <div v-if="patient.competing_npi" class="sensitive text-sm">
                      <div class="font-medium text-rose-600">{{ patient.competing_provider_name || patient.competing_npi }}</div>
                      <div class="text-xs text-gray-500">
                        <span v-if="patient.competing_provider_city">{{ patient.competing_provider_city }}, {{ patient.competing_provider_state }} · </span>
                        {{ patient.competing_npi_visits }} visit{{ patient.competing_npi_visits !== 1 ? 's' : '' }}
                      </div>
                    </div>
                    <span v-else class="text-gray-400 text-sm">-</span>
                  </td>
                  <td v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    <span v-if="patient.visits_after_loss > 0" class="text-amber-600">
                      {{ patient.visits_after_loss }} ({{ patient.visits_at_original_tin || 0 }} here)
                    </span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ patient.last_claim_date ? formatDate(patient.last_claim_date) : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-else class="text-center py-12 text-gray-500 text-sm">
              No patients found for this category
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div class="text-sm text-gray-500">
              Showing {{ patientList.length }} patients
              <span v-if="patientList.length >= 500" class="text-amber-600">(limited to 500)</span>
            </div>
            <button
              @click="closePatientModal"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import SankeyDiagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';

const FLOW_CATEGORIES = {
  retained: {
    title: 'Kept Patients',
  },
  new: {
    title: 'New Patients',
    color: '#3b82f6',       // blue-500
  },
  returning: {
    title: 'Returning Patients',
    color: '#06b6d4',       // cyan-500
  },
  lost_deceased: {
    title: 'Lost: Deceased',
    label: 'Deceased',
    description: 'Patient deceased',
    color: '#64748b',       // slate-500
  },
  lost_competition: {
    title: 'Lost: Competition',
    label: 'Competition',
    description: 'Getting care at other providers based on claims',
    color: '#f43f5e',       // rose-500
  },
  lost_disengaged: {
    title: 'Lost: Disengaged',
    label: 'Disengaged',
    description: 'No primary care claims anywhere after loss',
    color: '#fbbf24',       // amber-400
  },
  lost_still_active: {
    title: 'Lost: Still Active Here',
    label: 'Still Active Here',
    description: 'Still getting care here but lost attribution (contract/network change)',
    color: '#8b5cf6',       // violet-500 - stands out as anomaly
  },
  lost_unknown: {
    title: 'Lost: Unknown Reason',
    label: 'Unknown',
    description: 'No claims data available to determine reason',
    color: '#9ca3af',       // gray-400
  },
};

export default {
  name: 'Attribution_Health',
  components: {
    SankeyDiagram,
    ExportMenu,
    LoadingIndicator,
  },
  props: {
    entityId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      availableReports: [],  // Full list of contract+report objects from API
      selectedReportA: null,  // Now stores "CONTRACT_ID|REPORT_DT" key
      selectedReportB: null,  // Now stores "CONTRACT_ID|REPORT_DT" key
      reportAData: null,
      reportBData: null,
      flows: [],
      // Patient modal state
      showPatientModal: false,
      loadingPatients: false,
      patientList: [],
      selectedCategory: null,
      selectedFlow: null,
      exporting: false
    };
  },
  computed: {
    // All reports sorted by date descending, with a unique key
    sortedReports() {
      return this.availableReports
        .map(r => ({
          ...r,
          key: `${r.contract_id}|${r.report_dt}`
        }))
        .sort((a, b) => new Date(b.report_dt) - new Date(a.report_dt));
    },
    // Parse selected report A into contract_id and report_dt
    parsedReportA() {
      if (!this.selectedReportA) return null;
      const [contract_id, report_dt] = this.selectedReportA.split('|');
      return { contract_id, report_dt };
    },
    // Parse selected report B into contract_id and report_dt
    parsedReportB() {
      if (!this.selectedReportB) return null;
      const [contract_id, report_dt] = this.selectedReportB.split('|');
      return { contract_id, report_dt };
    },
    keptFlow() {
      return this.flows.find(f => f.category === 'retained');
    },
    newFlow() {
      return this.flows.find(f => f.category === 'new');
    },
    returningFlow() {
      return this.flows.find(f => f.category === 'returning');
    },
    lostFlows() {
      return this.flows.filter(f => f.category === 'lost').sort((a, b) => b.value - a.value);
    },
    competitionLoss() {
      const flow = this.lostFlows.find(f => f.target === 'lost_competition');
      return flow ? flow.value : 0;
    },
    disengagedLoss() {
      const flow = this.lostFlows.find(f => f.target === 'lost_disengaged');
      return flow ? flow.value : 0;
    },
    totalLost() {
      return this.lostFlows.reduce((sum, f) => sum + f.value, 0);
    },
    totalGained() {
      return (this.newFlow?.value || 0) + (this.returningFlow?.value || 0);
    },
    modalTitle() {
      if (!this.selectedCategory) return '';
      return FLOW_CATEGORIES[this.selectedCategory]?.title || this.selectedCategory;
    },
    modalSubtitle() {
      if (!this.selectedFlow) return '';
      return `${this.selectedFlow.value?.toLocaleString()} patients`;
    },
    netChange() {
      if (!this.reportAData || !this.reportBData) return 0;
      return this.reportBData.total_patients - this.reportAData.total_patients;
    },
    netChangePercent() {
      if (!this.reportAData || this.reportAData.total_patients === 0) return 0;
      return ((this.netChange / this.reportAData.total_patients) * 100).toFixed(1);
    },
    sankeyData() {
      if (!this.flows.length || !this.reportAData || !this.reportBData) return null;

      const nodes = [];
      const links = [];
      const nodeMap = new Map();
      let nodeIndex = 0;

      // Helper to get or create node
      const getNodeIndex = (id, name, color) => {
        if (nodeMap.has(id)) {
          return nodeMap.get(id);
        }
        const index = nodeIndex++;
        nodeMap.set(id, index);
        nodes.push({ name, color });
        return index;
      };

      // Create nodes
      const reportAIndex = getNodeIndex('report_a', `${this.formatReportDate(this.reportAData.date)}\n${this.reportAData.total_patients.toLocaleString()} patients`, '#6366f1');
      const reportBIndex = getNodeIndex('report_b', `${this.formatReportDate(this.reportBData.date)}\n${this.reportBData.total_patients.toLocaleString()} patients`, '#6366f1');

      // Process flows and create links
      this.flows.forEach(flow => {
        if (flow.category === 'retained') {
          // Report A → Report B (kept)
          links.push({
            source: reportAIndex,
            target: reportBIndex,
            value: flow.value
          });
        } else if (flow.category === 'lost') {
          // Report A → Loss category
          const lossNodeIndex = getNodeIndex(flow.target, `${this.getLossLabel(flow.target)}\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES[flow.target]?.color || '#9ca3af');
          links.push({
            source: reportAIndex,
            target: lossNodeIndex,
            value: flow.value
          });
        } else if (flow.category === 'new') {
          // New → Report B
          const newNodeIndex = getNodeIndex('new', `New\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES.new.color);
          links.push({
            source: newNodeIndex,
            target: reportBIndex,
            value: flow.value
          });
        } else if (flow.category === 'returning') {
          // Returning → Report B
          const returningNodeIndex = getNodeIndex('returning', `Returning\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES.returning.color);
          links.push({
            source: returningNodeIndex,
            target: reportBIndex,
            value: flow.value
          });
        }
      });

      return {
        nodes,
        links,
        stageLabels: [
          { label: 'Report A', position: 'left' },
          { label: 'Report B', position: 'right' }
        ]
      };
    },
    sankeyConfig() {
      return {
        margin: { top: 60, right: 200, bottom: 40, left: 200 },
        nodeWidth: 20,
        nodePadding: 30,
        iterations: 32,
        linkOpacity: 0.4,
        formatting: {
          percentage: false,
          sig_figs: 0
        }
      };
    }
  },
  mounted() {
    this.fetchComparison();
  },
  methods: {
    async fetchComparison() {
      this.loading = true;
      try {
        const payload = {
          entity_id: this.entityId
        };

        // Add parsed report A params
        if (this.parsedReportA) {
          payload.contract_a_id = this.parsedReportA.contract_id;
          payload.report_a_date = this.parsedReportA.report_dt;
        }

        // Add parsed report B params
        if (this.parsedReportB) {
          payload.contract_b_id = this.parsedReportB.contract_id;
          payload.report_b_date = this.parsedReportB.report_dt;
        }

        const response = await this.$axios.post('/api/portfolio_management/compare-reports/', payload);

        this.reportAData = response.data.report_a;
        this.reportBData = response.data.report_b;
        this.flows = response.data.flows;

        // Set available reports if returned
        if (response.data.available_reports) {
          this.availableReports = response.data.available_reports;

          // Sync dropdown selections to match the reports the backend actually used
          if (response.data.report_a) {
            this.selectedReportA = `${response.data.report_a.contract_id}|${response.data.report_a.date}`;
          }
          if (response.data.report_b) {
            this.selectedReportB = `${response.data.report_b.contract_id}|${response.data.report_b.date}`;
          }
        }
      } catch (error) {
        console.error('Error fetching report comparison:', error);
      } finally {
        this.loading = false;
      }
    },

    formatReportDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getLossLabel(target) {
      return FLOW_CATEGORIES[target]?.label || target;
    },

    getLossDescription(target) {
      return FLOW_CATEGORIES[target]?.description || '';
    },

    async viewPatients(flow) {
      // Determine category from flow
      let category;
      if (flow.category === 'lost') {
        category = flow.target;  // e.g., 'lost_competition'
      } else {
        category = flow.category;  // e.g., 'retained', 'new', 'returning'
      }

      this.selectedCategory = category;
      this.selectedFlow = flow;
      this.showPatientModal = true;
      this.loadingPatients = true;
      this.patientList = [];

      try {
        const payload = {
          entity_id: this.entityId,
          contract_a_id: this.parsedReportA?.contract_id,
          contract_b_id: this.parsedReportB?.contract_id,
          report_a_date: this.parsedReportA?.report_dt,
          report_b_date: this.parsedReportB?.report_dt,
          category: category,
          limit: 500
        };

        const response = await this.$axios.post('/api/portfolio_management/flow-patients/', payload);
        this.patientList = response.data;
      } catch (error) {
        console.error('Error fetching patients:', error);
        this.patientList = [];
      } finally {
        this.loadingPatients = false;
      }
    },

    closePatientModal() {
      this.showPatientModal = false;
      this.patientList = [];
      this.selectedCategory = null;
      this.selectedFlow = null;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    async exportCSV() {
      if (this.exporting || !this.flows.length) return;
      this.exporting = true;

      try {
        const response = await this.$axios.post('/api/portfolio_management/flow-patients/', {
          entity_id: this.entityId,
          contract_a_id: this.parsedReportA?.contract_id,
          contract_b_id: this.parsedReportB?.contract_id,
          report_a_date: this.parsedReportA?.report_dt,
          report_b_date: this.parsedReportB?.report_dt,
          category: 'all',
        });

        const rows = response.data.map(p => ({
          'Category': FLOW_CATEGORIES[p.flow_category]?.title || p.flow_category,
          'Last Name': p.bene_last_name || '',
          'First Name': p.bene_first_name || '',
          'DOB': p.bene_dob?.split('T')[0] || '',
          'Death Date': p.bene_death_dt?.split('T')[0] || '',
          'MBI': p.bene_mbi_id || '',
          'Facility': p.facility_name || '',
          'PCP': p.pcp_name || '',
          'Competing Provider': p.competing_provider_name || '',
          'Competing NPI': p.competing_npi || '',
          'Visits After Loss': p.visits_after_loss ?? '',
          'Last Claim Date': p.last_claim_date?.split('T')[0] || '',
          'Assignment Type': p.origin || '',
        }));

        const dateA = this.parsedReportA?.report_dt?.split(' ')[0];
        const dateB = this.parsedReportB?.report_dt?.split(' ')[0];
        const fileName = `attribution_health_${dateA}_${dateB}.xlsx`;
        await exportToExcel(rows, fileName, {
          sheetName: 'Attribution Health',
          columnWidths: {
            'Category': 140,
            'Last Name': 140,
            'First Name': 120,
            'DOB': 100,
            'Death Date': 100,
            'MBI': 120,
            'Facility': 175,
            'PCP': 175,
            'Competing Provider': 175,
            'Competing NPI': 105,
            'Visits After Loss': 105,
            'Last Claim Date': 100,
            'Assignment Type': 175,
          },
        });
      } catch (error) {
        console.error('Error exporting:', error);
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>
